import React, { useState } from 'react';
import { Plus, Star, Clock, MoreHorizontal } from 'lucide-react';
import type { PMReportPreset } from '../../../services/pmReportPresetService';

export interface PresetsBarProps {
    presets: PMReportPreset[];
    hasLastUsed: boolean;
    activeId: string | null | 'last-used';
    isModified: boolean;
    canSaveNew: boolean;
    onSelectPreset: (id: string) => void;
    onSelectLastUsed: () => void;
    onOpenSaveDialog: () => void;
    onUpdateActive: () => void;
    onRename: (id: string) => void;
    onDelete: (id: string) => void;
}

export const PresetsBar: React.FC<PresetsBarProps> = ({
    presets, hasLastUsed, activeId, isModified, canSaveNew,
    onSelectPreset, onSelectLastUsed, onOpenSaveDialog, onUpdateActive, onRename, onDelete,
}) => {
    const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);

    if (!hasLastUsed && presets.length === 0) return null;

    const pillClass = (active: boolean, modified: boolean) =>
        `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
            active && !modified
                ? 'border-emerald-600 bg-emerald-600 text-white'
                : active && modified
                ? 'border-emerald-600 bg-white text-emerald-700 dark:bg-slate-800'
                : 'border-slate-300 bg-white text-slate-700 hover:border-emerald-400 hover:bg-emerald-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
        }`;

    return (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
            {hasLastUsed && (
                <button
                    type="button"
                    aria-pressed={activeId === 'last-used'}
                    onClick={onSelectLastUsed}
                    className={pillClass(activeId === 'last-used', activeId === 'last-used' && isModified)}
                >
                    <Clock className="h-3.5 w-3.5" />
                    Last used
                    {activeId === 'last-used' && isModified && (
                        <span aria-label="modified from preset" className="ml-1 h-1.5 w-1.5 rounded-full bg-emerald-600" />
                    )}
                </button>
            )}

            {presets.map(preset => {
                const active = activeId === preset.id;
                return (
                    <div key={preset.id} className="relative inline-flex items-center">
                        <button
                            type="button"
                            aria-pressed={active}
                            onClick={() => onSelectPreset(preset.id)}
                            className={pillClass(active, active && isModified)}
                        >
                            <Star className="h-3.5 w-3.5" />
                            {preset.name}
                            {active && isModified && (
                                <span aria-label="modified from preset" className="ml-1 h-1.5 w-1.5 rounded-full bg-emerald-600" />
                            )}
                        </button>
                        <button
                            type="button"
                            aria-label={`Options for ${preset.name}`}
                            onClick={() => setMenuOpenFor(menuOpenFor === preset.id ? null : preset.id)}
                            className="ml-0.5 rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                        </button>
                        {menuOpenFor === preset.id && (
                            <div role="menu" className="absolute left-0 top-full z-10 mt-1 min-w-[180px] rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
                                {active && (
                                    <button role="menuitem" type="button" onClick={() => { setMenuOpenFor(null); onUpdateActive(); }} className="block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-700">
                                        Update with current form
                                    </button>
                                )}
                                <button role="menuitem" type="button" onClick={() => { setMenuOpenFor(null); onRename(preset.id); }} className="block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-700">
                                    Rename
                                </button>
                                <button role="menuitem" type="button" onClick={() => { setMenuOpenFor(null); onDelete(preset.id); }} className="block w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                );
            })}

            {isModified && activeId && activeId !== 'last-used' && (
                <button
                    type="button"
                    onClick={onUpdateActive}
                    className="text-[11px] font-semibold text-emerald-700 underline-offset-2 hover:underline"
                >
                    Update preset
                </button>
            )}

            <button
                type="button"
                onClick={onOpenSaveDialog}
                disabled={!canSaveNew}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:border-emerald-400 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
                title={canSaveNew ? 'Save current configuration as a preset' : 'Select projects and a date range to save a preset'}
            >
                <Plus className="h-3.5 w-3.5" />
                New preset
            </button>
        </div>
    );
};
